import React, { useEffect, useState } from 'react';
import { Builder } from '@builder.io/react';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import getSymbolFromCurrency from 'currency-symbol-map';
import { getPageType } from '../../../../../routes';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { selectSelectedCurrency } from '../../../../../store/user';
import { selectProducts } from '../../../../../store/products';
import useCart from '../../../../../hooks/cart/useCart';
import IsGTMScriptLoaded from '../../../../../utils/isGTMScriptLoaded';
import { checkDomain, shouldTrackTikTokEvent } from '../../../../../utils/checkDomain';
import sortProductsByCurrency from '../../../../../utils/productSortHandler';
import ProductCard from './productCard';
import MultiProductsBoxCart from '../../../../general/multiProductsBoxCart';
import { ProductBoxCart } from '../../../../../pages/home/components/productBoxCart';
import CustomButton from '../../../../general/button';

const ProductCardsListWithNavCard = ({
  products = [],
  sortByCurrency = false,
  videos = [],
  usaProducts = [],
  ukProducts = [],
  appealCardImage,
  appealCardTitle = '',
  appealCardDescription = '',
  appealCardButtonLink = '',
  appealCardButtonTitle = 'View Appeals',
  appealCardPosition,
}) => {
  const selectedCurrencyStore = useSelector(selectSelectedCurrency);
  const [selectedCurrency, setSelectedCurrency] = useState(selectedCurrencyStore || 'AUD');
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const _allProducts = useSelector(state => selectProducts(state));
  const { handleCheckProduct, handleAddToGiving, handleRemoveProduct } = useCart();
  const i18n = useTranslation();

  const [showMore, setShowMore] = useState(false);
  const [showMobileDescMap, setShowMobileDescMap] = React.useState({});

  const handleIsAnyAmount = product => {
    const hasQuantity = product.quantity ? product.quantity > 1 : false;
    return Number(product.aud) <= 2 && !hasQuantity;
  };

  const handleViewItemDataLayer = (tempProduct, currency) => {
    const dataLayerTemp = {
      event: 'view_item',
      ecommerce: {
        currency: currency.toUpperCase(),
        items: [
          {
            item_id: tempProduct.creator,
            item_name: tempProduct.name,
            item_brand: tempProduct.category || '',
            item_category: tempProduct.category || '',
            item_description: tempProduct.description || '',
            price: tempProduct[currency.toLowerCase()],
          },
        ],
      },
    };
    window.dataLayer = window.dataLayer || [];
    if (IsGTMScriptLoaded()) {
      window.dataLayer.push(dataLayerTemp);
    }
    const obj = {
      content_type: 'product',
      content_id: tempProduct.creator,
      currency: currency,
      value: tempProduct[currency.toLowerCase()],
      price: tempProduct[currency.toLowerCase()],
      quantity: 1,
      content_category: tempProduct.category || 'donation',
      content_name: tempProduct.name,
      description: tempProduct.description,
    };
    if (window.ttq) {
      if (shouldTrackTikTokEvent()) {
        window.ttq.instance(localStorage.getItem('TikTokID')).track('ViewContent', obj);
      }
    }
    if (!window.adobeDataLayer) {
      window.adobeDataLayer = [];
    }
    window.adobeDataLayer.push({
      event: 'impression_item',
      page_info: {
        page_url: window.location.href,
        page_title: document.title,
        page_type: getPageType(window.location.pathname),
        language: i18n.language
      },
      items: [{
        item_id: tempProduct.creator,
        item_name: tempProduct.name,
        item_brand: tempProduct.category ? tempProduct.category : "",
        item_category: tempProduct.category ? tempProduct.category : "",
        item_description: tempProduct.description || '',
        price: tempProduct[currency.toLowerCase()],
        quantity: 1,
        currency: currency.toUpperCase()
      }]
    });
  };

  const ShowCurrencies = (currency, _price) => {
    const tempCurrency = currency?.toUpperCase();
    let price = Number(_price);
    const options = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };
    if (price && Number(price) % 1 !== 0) {
      price = Number(price).toLocaleString('en', options);
    }
    let final = '';
    price = price ? price.toLocaleString() : '0';
    switch (tempCurrency) {
      case 'GBP':
      case 'EUR':
        final = `${getSymbolFromCurrency(tempCurrency.toLowerCase())}${price}`;
        break;
      case 'MYR':
        final = `${price}`;
        break;
      case 'IDR':
        final = `Rp${price}`;
        break;
      case 'AED':
        final = `Dh${price}`;
        break;
      default:
        final = `$${price}`;
        break;
    }
    return final;
  };

  const getSelectedProducts = _allProducts => {
    setIsLoaded(true);
    const tempSelectedProducts = [];
    let tempVideos = [];
    const productsArray = typeof products === 'string' ? products.split(',') : products;
    const usaProductsArray = typeof usaProducts === 'string' ? usaProducts.split(',') : usaProducts;
    const ukProductsArray = typeof ukProducts === 'string' ? ukProducts.split(',') : ukProducts;
    _allProducts.forEach(item => {
      item.sub_categories.forEach(innerItem => {
        innerItem.products.forEach(innerProduct => {
          if (productsArray.includes(innerProduct.creator)) {
            tempSelectedProducts.push(innerProduct);
          }
          if (checkDomain('matwprojectusa.org') && usaProductsArray.includes(innerProduct.creator)) {
            tempSelectedProducts.push(innerProduct);
          }
          if (checkDomain('matwproject.org.uk') && ukProductsArray.includes(innerProduct.creator)) {
            tempSelectedProducts.push(innerProduct);
          }
        });
      });
    });
    const combinedProducts = [...productsArray, ...usaProductsArray, ...ukProductsArray];
    const sortedByCreator = tempSelectedProducts.sort((a, b) => {
      return combinedProducts.indexOf(a.creator) - combinedProducts.indexOf(b.creator);
    });
    const uniqueProducts = sortedByCreator.reduce((acc, current) => {
      const existingProduct = acc.find(item => item.creator === current.creator);
      if (!existingProduct) {
        acc.push(current);
      }
      return acc;
    }, []);
    if (videos.length > 0) {
      tempVideos = videos.split(',').map(video => ({
        video_src: video.trim(),
      }));
    }
    setSelectedProducts(uniqueProducts);
    setSelectedVideos(tempVideos);
  };

  useEffect(() => {
    if (products.length > 0) {
      _allProducts && Object.keys(_allProducts).length && getSelectedProducts(_allProducts);
    }
  }, [_allProducts, products]);

  useEffect(() => {
    setSelectedCurrency(selectedCurrencyStore || 'AUD');
  }, [selectedCurrencyStore]);

  const sortedProducts = sortByCurrency
    ? sortProductsByCurrency([...selectedProducts], selectedCurrency.toLowerCase())
    : [...selectedProducts];

  // Insert the appeal card at the specified position
  let cards = sortedProducts.map((product, index) => ({ type: 'product', product, index }));
  const appealCard = {
    type: 'appeal',
    key: 'appeal-card',
    props: {
      image: appealCardImage,
      title: appealCardTitle,
      description: appealCardDescription,
      buttonLink: appealCardButtonLink,
      buttonTitle: appealCardButtonTitle,
    },
  };
  const pos = typeof appealCardPosition === 'number' ? appealCardPosition : cards.length;
  cards.splice(pos, 0, appealCard);

  return (
    <div className="flex flex-wrap gap-3 md:gap-4 justify-center items-start w-full">
      {isLoaded ? (
        cards.length > 0 ? (
          cards.map((item, idx) => {
            if (item.type === 'product') {
              return (
                <div key={`product-${item.product.creator}-${item.index}`} className=" w-full sm:max-w-[300px] sm:basis-[calc(50%-8px)] hidden sm:block">
                  <ProductCard
                    product={item.product}
                    alt={''}
                    buttonLabel={''}
                    _lang={'en'}
                    removeDataLayer={false}
                    isVisibleDescription={true}
                    roundedButton={false}
                    squareButton={false}
                    selectedCurrencyStore={selectedCurrencyStore}
                    handleViewItemDataLayer={handleViewItemDataLayer}
                    handleIsAnyAmount={handleIsAnyAmount}
                    ShowCurrencies={ShowCurrencies}
                  />
                </div>
              );
            } 
            else if (item.type === 'appeal') {
              return (
                <div key={item.key} className="w-full sm:max-w-[300px] sm:basis-[calc(50%-8px)] bg-white shadow-lg overflow-hidden border border-stone-200  md:flex-col items-center h-full flex-1 hidden sm:block">
                  {/* Image Section with zoom/hover */}
                  <div className="overflow-hidden img-hover-zoom w-full sm:min-h-[169px] md:min-h-[210px] lg:min-h-[301px] xl:min-h-[280px] 2xl:min-h-[344px] tooltip [&>span]:!block cursor-pointer min-w-[260px] flex justify-center items-center">
                    {item.props.image ? (
                      <img
                        src={item.props.image}
                        alt={item.props.title}
                        className="cursor-pointer block w-full h-full object-cover"
                      />
                    ) : (
                      <Skeleton height={200} width={320} className="mb-3" />
                    )}
                  </div>
                  {/* For the title: */}
                  <p className={
                    `text-[#00a3da] cursor-pointer flex justify-center items-center text-sm sm:text-md md:text-md lg:text-lg xl:text-xl text-center px-1 mt-3 min-h-[56px] uppercase font-medium`
                  }>
                    {item.props.title ? item.props.title : <Skeleton height={56} />}
                  </p>
                  <div className="relative text-center w-full">
                    {item.props.description ? (
                      <>
                        <div
                          className={`transition-all duration-300 text-center my-2 relative overflow-hidden ${showMore ? 'h-auto mb-4' : 'max-h-[65px] md:max-h-[78px]'}`}
                        >
                          <p
                            className={`text-stone-500 text-sm sm:text-sm md:text-base lg:text-base overflow-hidden transition-all duration-200 min-h-[65px] md:min-h-[79px] px-2 md:px-3`}
                          >
                            {item.props.description}
                          </p>
                        </div>
                        <ChevronDownIcon
                          onClick={() => setShowMore((prev) => !prev)}
                          className={`md:block w-6 h-6 text-[#00a3da] transition-all duration-300 cursor-pointer mx-auto ${showMore ? 'transform rotate-180' : ''}`}
                        />
                      </>
                    ) : (
                      <Skeleton count={4} />
                    )}
                  </div>
                  <div className="flex-1"></div>
                  <div className="flex justify-center pb-3 md:pb-5 mt-[21px] w-full">
                    {item.props.buttonLink ? (
                      <CustomButton
                        className="!rounded-none"
                        onClick={() => {
                          window.open(item.props.buttonLink, '_blank');
                        }}
                        title={item.props.buttonTitle}
                      />
                    ) : (
                      <Skeleton height={43} width={140} />
                    )}
                  </div>
                </div>
              );
            }
            return null;
          })
        ) : (
          <p className="text-center w-full">No products selected.</p>
        )
      ) : (
        <Skeleton count={4} />
      )}
      {/* Mobile rendering for all cards, including appeal card */}
      {isLoaded && cards.length > 0 && (
        cards.map((item, idx) => {
          if (item.type === 'product') {
            const product = item.product;
            return (
              <div key={`product-mobile-${product.creator}-${idx}`} className="w-full sm:hidden">
                {product.products ? (
                  <MultiProductsBoxCart
                    product={product}
                    multiProducts={product.products}
                    staticLink={'/'}
                    redirectUrl={product.redirectUrl}
                    title={product.title}
                    description={product.description}
                    image={product.img}
                    _lang={'eng'}
                    alt={product?.alt}
                    currency={selectedCurrencyStore}
                    onCheck={handleCheckProduct}
                    onAdd={handleAddToGiving}
                    onRemove={handleRemoveProduct}
                  />
                ) : (
                  <ProductBoxCart
                    product={product}
                    onCheck={handleCheckProduct}
                    onAdd={handleAddToGiving}
                    onRemove={handleRemoveProduct}
                    currency={selectedCurrencyStore}
                    stateVariableObj={{ productName: product.name }}
                    _lang={'eng'}
                    linkBtnClasses={product.linkBtnClasses || ''}
                    isStaticProduct={['Zakat Calculator'].includes(product.product_type)}
                    staticLink={product.product_type ? product.short_description : ''}
                    staticLinkText={product.product_type ? product.name : ''}
                  />
                )}
              </div>
            );
          } 
          else if (item.type === 'appeal') {
            // Appeal card UI for mobile: button always at bottom, even when description is hidden
            const showMobileDesc = showMobileDescMap[item.key] || false;
            return (
              <div key={`appeal-mobile-${item.key}`} className={`w-full sm:hidden bg-white shadow-lg overflow-hidden border border-stone-200 flex flex-row p-0 transition-all duration-300`} style={{ minHeight: showMobileDesc ? 140 : 90 }}>
                {/* Image Section: flush left, no border radius, no padding */}
                <div className="flex-shrink-0 w-[90px] h-full">
                  {item.props.image ? (
                    <img
                      src={item.props.image}
                      alt={item.props.title}
                      className="block w-full h-full object-cover object-center"
                    />
                  ) : (
                    <Skeleton height={90} width={90} />
                  )}
                </div>
                {/* Content Section: column flex, button always at bottom */}
                <div className={`flex-1 flex flex-col justify-between relative ${showMobileDesc ? '' : 'min-h-[90px]'}`}>
                  <div>
                    {/* Title and chevron at top left with small top padding */}
                    <div className="flex flex-row items-center gap-2 pt-2 pl-4">
                      <span className="text-[#00a3da] font-bold text-[16px] leading-tight">{item.props.title ? item.props.title : <Skeleton width={70} height={16} />}</span>
                      {item.props.description && (
                        <button
                          type="button"
                          aria-label="Show description"
                          className="focus:outline-none"
                          onClick={() => setShowMobileDescMap(prev => ({ ...prev, [item.key]: !showMobileDesc }))}
                        >
                          <ChevronDownIcon className={`w-4 h-4 text-[#00a3da] transition-transform duration-300 ${showMobileDesc ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {/* Description (collapsible) */}
                    {item.props.description && (
                      <div
                        className={`overflow-hidden transition-all duration-300 px-4 ${showMobileDesc ? 'max-h-[120px] mt-1' : 'max-h-0'} text-xs text-stone-600`}
                        style={{
                          minHeight: showMobileDesc ? 32 : 0,
                          opacity: showMobileDesc ? 1 : 0,
                        }}
                      >
                        {item.props.description}
                      </div>
                    )}
                  </div>
                  {/* Button at bottom right with small right/bottom padding */}
                  <div className="flex justify-end pr-2 pb-2">
                    {item.props.buttonLink ? (
                      <button
                        className="bg-[#ff0066] text-white font-extrabold rounded-none px-4 py-1.5 text-sm leading-none shadow-md hover:bg-[#e6005c] transition-colors"
                        style={{ minWidth: 90 }}
                        onClick={() => window.open(item.props.buttonLink, '_blank')}
                      >
                        {item.props.buttonTitle}
                      </button>
                    ) : (
                      <Skeleton height={28} width={90} />
                    )}
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })
      )}
    </div>
  );
};

Builder.registerComponent(ProductCardsListWithNavCard, {
  name: 'ProductCardsListWithNavCard',
  inputs: [
    { name: 'products', type: 'string', helperText: 'Comma-separated product creator IDs' },
    { name: 'sortByCurrency', type: 'boolean', defaultValue: true },
    { name: 'videos', type: 'string' },
    { name: 'ukProducts', type: 'string' },
    { name: 'usaProducts', type: 'string' },
    // Appeal card props:
    { name: 'appealCardImage', type: 'file', allowedFileTypes: ['jpg', 'jpeg', 'png', 'webp'], friendlyName: 'Appeal Card Image' },
    { name: 'appealCardTitle', type: 'string', friendlyName: 'Appeal Card Title' },
    { name: 'appealCardDescription', type: 'text', friendlyName: 'Appeal Card Description' },
    { name: 'appealCardButtonLink', type: 'string', friendlyName: 'Appeal Card Button Link' },
    { name: 'appealCardButtonTitle', type: 'string', friendlyName: 'Appeal Card Button Title', defaultValue: 'View Appeals' },
    { name: 'appealCardPosition', type: 'number', friendlyName: 'Appeal Card Position', helperText: 'Index to insert the appeal card (0-based, default: end)' },
  ],
});

export default ProductCardsListWithNavCard;
